import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect, useSelector } from 'react-redux';
import cn from 'classnames';
import {
  Modal,
  ModalBody,
  ModalHeader,
} from 'reactstrap';
import { Button } from 'components/Button';
import { ToastContainer, toast } from 'react-toastify';
import { Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import IdleTimer from 'react-idle-timer';
import isEmpty from 'lodash/isEmpty';
import { Header } from 'components/Header';
import Footer from 'components/Footer';
import ExternalRedirect from 'components/ExternalRedirect';
import { signOut } from 'actions/auth';
import { useConfig } from 'config/ConfigContext';
import {
  mfaGuardRedirect, twoFactorGuardRedirect, AUTH_FLOW_FLAG,
  AUTH_STEP_CHALLENGE, AUTH_STEP_SETUP,
} from 'utils/aws';
import Login from './pages/Auth/Login';
import SetNewPassword from './pages/Auth/SetNewPassword';
import Forgot from './pages/Auth/Forgot';
import ForgotUsername from './pages/Auth/ForgotUsername';
import ChangePassword from './pages/Auth/ChangePassword';
import Registration from './pages/Registration/Registration';
import RegistrationConfirm from './pages/Registration/RegistrationConfirm';
import RegistrationSuccess from './pages/Registration/RegistrationSuccess';
import EmailVerification from './pages/TwoFactor/EmailVerification';
import VerificationSuccess from './pages/TwoFactor/VerificationSuccess';
import TwoFactorSetupOptions from './pages/TwoFactor/TwoFactorSetupOptions';
import OfferTwoFactor from './pages/TwoFactor/OfferTwoFactor';
import SetupAuthenticatorApp from './pages/TwoFactor/SetupAuthenticatorApp';
import VerifyByEmail from './pages/TwoFactor/VerifyByEmail';
import VerifyAuthenticatorCode from './pages/TwoFactor/VerifyAuthenticatorCode';
import WhatIsAuthenticatorApp from './pages/TwoFactor/WhatIsAuthenticatorApp';
import Dashboard from './pages/Dashboard';
import MyLoans from './pages/MyLoans';
import Documents from './pages/Documents';
import Settings from './pages/Settings';
import FAQs from './pages/FAQs';
import SimplePay from './pages/QuickPay/SimplePay';
import LoverYourLtv from './pages/QuickPay/LowerYourLtv';
import AddPaymentMethod from './pages/QuickPay/AddPaymentMethod';
import PaymentSubmitted from './pages/QuickPay/PaymentSubmitted';
import EnrollAutopay from './pages/QuickPay/EnrollAutopay';
import AutopayScheduled from './pages/QuickPay/AutopayScheduled';
import LinkExpired from './pages/QuickPay/LinkExpired';
import QuickpayLanding from './pages/QuickPay/Landing';
import BorrowerSelfIdentification from './pages/QuickPay/BorrowerSelfIdentification';
import UpdateDebitCard from './pages/QuickPay/UpdateDebitCard';
import './App.scss';
import ContractModificationDoc from './pages/ESignContactModification';
import ESignContarctModificationCompelete from './pages/ESignContactModification/complete';
import SettlementEignContactModification from './pages/SettlementEsignDoc';
import SettlementContractCompelete from './pages/SettlementEsignDoc/complete';
import ScraWaiverEsignDoc from './pages/ScraWaiverEsignDoc';
import ScraWaiverComplete from './pages/ScraWaiverEsignDoc/complete';

const PublicRoute = ({ component: InternalComponent, ...rest }) => {
  const location = useLocation();

  const token = sessionStorage.getItem('idToken');

  if (token) {
    return <Navigate to="/consumer/dashboard" state={{ from: location }} replace />;
  }

  return <InternalComponent {...rest} />;
};

const MultiRoute = ({ component: InternalComponent, ...rest }) => (
  <InternalComponent {...rest} />
);

// Guards the two-factor / setup screens (incl. the authenticator QR screen).
// These render before a full session exists (mid-sign-in challenge, MFA setup,
// or registration email verification), so they can't be gated on idToken alone.
// twoFactorGuardRedirect allows render when there is an authenticated session
// OR an auth flow is genuinely in progress; otherwise it redirects to sign-in,
// so the screens can't be opened cold by typing their URL.
const TwoFactorRoute = ({ component: InternalComponent, allowedSteps, ...rest }) => {
  const { redirect } = twoFactorGuardRedirect(
    sessionStorage.getItem('idToken'),
    sessionStorage.getItem(AUTH_FLOW_FLAG),
    allowedSteps
  );

  if (redirect) {
    return <Navigate to={redirect} replace />;
  }

  return <InternalComponent {...rest} />;
};

// Enforces mandatory MFA setup before granting dashboard access (Req 5.4).
// Composes the idToken gate with the mfaSatisfied flag using the pure
// mfaGuardRedirect helper: no idToken -> sign-in; not satisfied -> MFA setup.
const MfaGuard = ({ component: InternalComponent, ...rest }) => {
  const { redirect } = mfaGuardRedirect(
    sessionStorage.getItem('idToken'),
    sessionStorage.getItem('mfaSatisfied')
  );

  if (redirect) {
    return <Navigate to={redirect} replace />;
  }

  return <InternalComponent {...rest} />;
};

// Catch-all fallback (Req 9.3): notify the user the requested screen was not
// found, then redirect to a defined screen so they never land on a dead end.
const NotFoundRedirect = () => {
  useEffect(() => {
    toast.error('The requested page was not found.');
  }, []);

  return <Navigate to="/" replace />;
};

const QuickpayRoute = ({ component: Component, ...rest }) => {
  const location = useLocation();
  const params = useParams();
  const quickpayData = useSelector((state) => state.quickpay?.data);
  const uid = useSelector((state) => state.quickpay?.uid);
  let redirect = '/';
  let from = location;

  if ((uid || params.uid) && isEmpty(quickpayData)) {
    redirect = `/quickpay/${params.uid || uid}`;
  }

  if (location.pathname.includes(params.uid)) {
    from = {
      ...from,
      pathname: location.pathname.split('/').slice(0, -1).join('/'),
    };
  }

  if (!quickpayData?.loanId) {
    const preservedState = typeof location.state === 'object' && location.state !== null ? location.state : {};
    return (
      <Navigate
        to={{
          pathname: redirect,
          state: { ...preservedState, from },
        }}
      />
    );
  }

  return <Component {...rest} />;
};

const routes = [
  {
    name: 'Login',
    pathname: '/',
    component: Login,
    exact: true,
    wrapper: PublicRoute,
  },
  {
    name: 'Forgot',
    pathname: '/forgot',
    component: Forgot,
    exact: true,
    wrapper: PublicRoute,
  },
  {
    name: 'Forgot Username',
    pathname: '/forgot-username',
    component: ForgotUsername,
    exact: true,
    wrapper: PublicRoute,
  },
  {
    name: 'New Password',
    pathname: '/set-new-password',
    component: SetNewPassword,
    exact: true,
    wrapper: PublicRoute,
  },
  {
    name: 'Registration',
    pathname: '/registration',
    component: Registration,
    exact: true,
    wrapper: PublicRoute,
  },
  {
    name: 'RegistrationConfirm',
    pathname: '/registration-confirm',
    component: RegistrationConfirm,
    exact: true,
    wrapper: PublicRoute,
  },
  {
    name: 'RegistrationSuccess',
    pathname: '/registration-success',
    component: RegistrationSuccess,
    exact: true,
    wrapper: PublicRoute,
  },
  // Two-factor authentication flow (sign-in-time / setup screens).
  // TwoFactorRoute: the user may be mid-challenge (not fully signed in), in
  // registration email verification, or an authenticated user setting up 2FA.
  // These are not token-gated (no full session yet), but they are guarded so a
  // not-signed-in user can't open them directly by URL: render requires either
  // an idToken or an in-progress auth flow, else redirect to sign-in.
  {
    name: 'Email Verification',
    pathname: '/two-factor/verify-email',
    component: EmailVerification,
    exact: true,
    wrapper: TwoFactorRoute,
    allowedSteps: [AUTH_STEP_CHALLENGE],
  },
  {
    name: 'Verification Successful',
    pathname: '/two-factor/verification-success',
    component: VerificationSuccess,
    exact: true,
    wrapper: TwoFactorRoute,
  },
  {
    name: 'Two-Factor Offer',
    pathname: '/two-factor/offer',
    component: OfferTwoFactor,
    exact: true,
    wrapper: TwoFactorRoute,
  },
  {
    name: '2FA Setup Options',
    pathname: '/two-factor/setup',
    component: TwoFactorSetupOptions,
    exact: true,
    wrapper: TwoFactorRoute,
  },
  {
    name: 'Set Up Authenticator App',
    pathname: '/two-factor/setup-authenticator',
    component: SetupAuthenticatorApp,
    exact: true,
    wrapper: TwoFactorRoute,
    // QR/setup screen: only when enrolment is required (setup step) or an
    // authenticated user re-setting up from Settings (idToken, handled in guard).
    allowedSteps: [AUTH_STEP_SETUP],
  },
  {
    name: 'Verify By Email',
    pathname: '/two-factor/verify-by-email',
    component: VerifyByEmail,
    exact: true,
    wrapper: TwoFactorRoute,
    // Code-entry step, or an authenticated user enabling email MFA (idToken).
    allowedSteps: [AUTH_STEP_CHALLENGE],
  },
  {
    name: 'Verify Authenticator Code',
    pathname: '/two-factor/verify',
    component: VerifyAuthenticatorCode,
    exact: true,
    wrapper: TwoFactorRoute,
    allowedSteps: [AUTH_STEP_CHALLENGE],
  },
  {
    name: 'What Is An Authenticator App',
    pathname: '/two-factor/authenticator-info',
    component: WhatIsAuthenticatorApp,
    exact: true,
    wrapper: TwoFactorRoute,
    // Help screen reached from the setup/QR path (or by an authenticated user).
    allowedSteps: [AUTH_STEP_SETUP],
  },
  {
    name: 'TermsOfUse',
    pathname: '/terms-of-use',
    component: () => <ExternalRedirect to="https://lendingusa.com/terms-of-use/" />,
    exact: true,
    wrapper: MultiRoute,
  },
  {
    name: 'PrivacyPolicy',
    pathname: '/privacy-policy',
    component: () => <ExternalRedirect to="https://lendingusa.com/privacy-policy/" />,
    exact: true,
    wrapper: MultiRoute,
  },
  // end Internal pages
  {
    name: 'Dashboard',
    pathname: '/consumer/dashboard',
    component: Dashboard,
    wrapper: MfaGuard,
  },
  {
    name: 'My Loans',
    pathname: '/consumer/loans',
    component: MyLoans,
    wrapper: MfaGuard,
  },
  {
    name: 'Contract Modification Agreement',
    pathname: '/sign/modification/loan/:loanId/contract/:contractId/key/:key',
    component: ContractModificationDoc,
    wrapper: PublicRoute,
  },
  {
    name: 'Complete ESign Contract Modification',
    pathname: '/sign/modification/loan/:loanId/complete',
    component: ESignContarctModificationCompelete,
    wrapper: PublicRoute,
  },
  {
    name: 'Settlement ESign Settlement Modification',
    pathname: '/sign/settlement/loan/:loanId/settlement/:settlementId/key/:key',
    component: SettlementEignContactModification,
    wrapper: PublicRoute,
  },
  {
    name: 'Complete ESign Settlement Modification',
    pathname: '/sign/settlement/loan/:loanId/complete',
    component: SettlementContractCompelete,
    wrapper: PublicRoute,
  },
  {
    name: 'SCRA Waiver ESign',
    pathname: '/sign/scra-waiver/loan/:loanId/scra/:scraId/key/:key',
    component: ScraWaiverEsignDoc,
    wrapper: PublicRoute,
  },
  {
    name: 'Complete ESign SCRA Waiver',
    pathname: '/sign/scra-waiver/loan/:loanId/complete',
    component: ScraWaiverComplete,
    wrapper: PublicRoute,
  },
  {
    name: 'Documents',
    pathname: '/consumer/documents',
    component: Documents,
    wrapper: MfaGuard,
  },
  {
    name: 'FAQs',
    pathname: '/consumer/faqs',
    component: FAQs,
    wrapper: MfaGuard,
  },
  {
    name: 'Settings',
    pathname: '/consumer/settings',
    component: Settings,
    wrapper: MfaGuard,
  },
  {
    name: 'Reset Password',
    pathname: '/dashboard/reset-password',
    component: ChangePassword,
    exact: true,
    wrapper: MfaGuard,
  },
  // Quickpay routes
  {
    name: 'Simple Pay',
    pathname: '/quickpay/simple-pay',
    component: SimplePay,
    wrapper: QuickpayRoute,
  },
  {
    name: 'Update Debit Card',
    pathname: '/quickpay/update-card/:paymentAccountId',
    component: UpdateDebitCard,
    wrapper: QuickpayRoute,
  },
  {
    name: 'Update Debit Card with UID',
    pathname: '/quickpay/update-card/:paymentAccountId/:uid',
    component: UpdateDebitCard,
    exact: true,
    wrapper: QuickpayRoute,
  },

  {
    name: 'Lower Your Ltv',
    pathname: '/quickpay/lower-your-ltv',
    component: LoverYourLtv,
    wrapper: QuickpayRoute,
  },
  {
    name: 'Lower Your Ltv with UID',
    pathname: '/quickpay/lower-your-ltv/:uid',
    component: LoverYourLtv,
    exact: true,
    wrapper: QuickpayRoute,
  },
  {
    name: 'Add Payment Methods',
    pathname: '/quickpay/add-payment-method',
    component: AddPaymentMethod,
    exact: true,
    wrapper: QuickpayRoute,
  },
  {
    name: 'Add Payment Methods',
    pathname: '/quickpay/add-payment-method/:uid',
    component: AddPaymentMethod,
    exact: true,
    wrapper: QuickpayRoute,
  },
  {
    name: 'Payment Submitted',
    pathname: '/quickpay/payment-submitted',
    component: PaymentSubmitted,
    exact: true,
    wrapper: QuickpayRoute,
  },
  {
    name: 'Enroll in Autopay',
    pathname: '/quickpay/enroll-autopay',
    component: EnrollAutopay,
    exact: true,
    wrapper: QuickpayRoute,
  },
  {
    name: 'Autopay Scheduled',
    pathname: '/quickpay/autopay-scheduled',
    component: AutopayScheduled,
    exact: true,
    wrapper: QuickpayRoute,
  },
  {
    name: 'Link Expired',
    pathname: '/quickpay/link-expired',
    component: LinkExpired,
    exact: true,
    wrapper: MultiRoute,
  },
  {
    name: 'Borrower Self-Identification (direct / dev)',
    pathname: '/quickpay/self-identification',
    component: BorrowerSelfIdentification,
    exact: true,
    wrapper: MultiRoute,
  },
  {
    name: 'Borrower Self-Identification',
    pathname: '/quickpay/self-identification/:uid',
    component: BorrowerSelfIdentification,
    exact: true,
    wrapper: MultiRoute,
  },
  {
    name: 'Quickpay',
    pathname: '/quickpay/:uid',
    component: QuickpayLanding,
    exact: true,
    wrapper: MultiRoute,
  },
];

const extendedFooters = [
  '/dashboard/text-apply',
  '/applications/dtm/application',
];

export const ParentRoute = (props) => {
  const { env, sessionTimeoutInMinutes } = useConfig();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [timeoutId, setTimeoutId] = useState(null);
  const idleTimer = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogoutModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const addNoIndexMetaTag = () => {
    if (document.querySelector('meta[name="robots"]')) {
      return;
    }

    const metaTag = document.createElement('meta');
    metaTag.name = 'robots';
    metaTag.content = 'noindex, nofollow';
    document.head.appendChild(metaTag);
  };

  useEffect(() => {
    if (env && env !== 'production') {
      addNoIndexMetaTag();
    }
  }, []);

  const signout = () => {
    setIsModalOpen(false);
    sessionStorage.getItem('idToken') && props.signOut(navigate);
  };

  const onIdle = () => {
    if (sessionStorage.getItem('idToken')) {
      const newTimeoutId = setTimeout(
        () => {
          signout();
        },
        2 * 60 * 1000
      );

      setIsModalOpen(true);
      setTimeoutId(newTimeoutId);
    }
  };

  const renderFooter = () => {
    const pathname = location?.pathname || '';
    if (pathname.indexOf('quickpay') !== -1) {
      return null;
    }

    return <Footer isContentVisible={extendedFooters.indexOf(pathname) !== -1} />;
  };

  return (
    <IdleTimer
      ref={idleTimer}
      element={document}
      onIdle={onIdle}
      timeout={1000 * 60 * (sessionTimeoutInMinutes - 2)}
    >
      <div className={cn('App', location.pathname.indexOf('quickpay') !== -1 ? 'mobile' : ' bg-light')}>
        <Header />
        <div className={cn('container-body corporate')}>
          <Routes>
            {routes.map((route, index) => {
              const Wrapper = route.wrapper;
              return (
                <Route
                  key={index}
                  path={route.pathname}
                  element={<Wrapper component={route.component} allowedSteps={route.allowedSteps} />}
                />
              );
            })}
            <Route path="*" element={<NotFoundRedirect />} />
          </Routes>
        </div>
        {renderFooter()}
        <Modal isOpen={isModalOpen} toggle={handleLogoutModal} size="md">
          <ModalHeader toggle={handleLogoutModal}>
            <button type="button" className="close" onClick={handleLogoutModal} aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </ModalHeader>
          <ModalBody className="text-center">
            <p className="mb-3">You will be automatically logged off after 2 minutes. If you would like to continue click on the Continue button.</p>
            <Button
              onClick={() => {
                clearTimeout(timeoutId);
                signout();
              }}
              color="danger"
              className="w-100 mb-1"
            >
              Logout
            </Button>
            <Button
              onClick={() => {
                clearTimeout(timeoutId);
                handleLogoutModal();
              }}
              className="w-100"
            >
              Continue
            </Button>
          </ModalBody>
        </Modal>
        <ToastContainer position="top-center" />
      </div>
    </IdleTimer>
  );
};

PublicRoute.propTypes = {
  component: PropTypes.any.isRequired,
};

QuickpayRoute.propTypes = {
  component: PropTypes.any.isRequired,
};

MultiRoute.propTypes = {
  component: PropTypes.any.isRequired,
};

TwoFactorRoute.propTypes = {
  component: PropTypes.any.isRequired,
  allowedSteps: PropTypes.arrayOf(PropTypes.string),
};

TwoFactorRoute.defaultProps = {
  allowedSteps: [],
};

MfaGuard.propTypes = {
  component: PropTypes.any.isRequired,
};

ParentRoute.propTypes = {
  signOut: PropTypes.func.isRequired,
};

export default compose(
  connect(
    (state) => ({
      auth: state.auth,
    }),
    {
      signOut,
    }
  )
)(ParentRoute);
